import { Link } from "react-router-dom";
import { ArrowRight, Video, MapPin, Layers, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LineReveal } from "@/components/LineReveal";
import { LeadForm } from "@/components/LeadForm";
import { BrandImage } from "@/components/Brand";
import { SERVICES } from "@/data/services";

const REGIONS = [
  "London", "South East", "South West", "Midlands", "East of England",
  "North West", "North East", "Scotland", "Wales", "Northern Ireland",
];

const WAYS = [
  {
    icon: Video,
    title: "Remote-first, human always",
    text: "Phone, video, email, whichever gets you the answer fastest. You talk to the person doing the work, never a call handler reading a script.",
  },
  {
    icon: MapPin,
    title: "On site when it counts",
    text: "Investigations, restructures and the meetings that can't go wrong: we travel. Remote for the day-to-day, in the room for the decisive moments.",
  },
  {
    icon: Layers,
    title: "One team, whole picture",
    text: "No postcode lottery of local reps. One relationship, one consistent standard of advice, and your whole history in one place.",
  },
];

const NATIONAL_CASES = [
  { tag: "Tech", title: "A Manchester SaaS startup, 18 staff", result: "First disciplinary handled end-to-end by video, fair, documented, closed without escalation." },
  { tag: "Care", title: "An Edinburgh care provider", result: "Contracts and handbook rebuilt for shift work reality; sickness absence finally under control." },
  { tag: "Retail", title: "A Bristol retail group, three sites", result: "One HR framework across all locations, managers trained once, consistently, everywhere." },
];

export default function Uk() {
  const origin = window.location.origin;

  return (
    <main data-testid="uk-page">
      <Seo
        title="HR Consultancy UK, National SME HR Support | Electra HR"
        siteName="Electra HR"
        description="National HR consultancy for UK founders and SMEs of 5 to 50 people. Same-day answers by phone and video, fixed fees, no retainers, backed by Bonsai Law."
        image="/api/og/uk.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ProfessionalService",
              "@id": `${origin}/#uk`,
              name: "Electra HR, HR Consultancy UK",
              image: `${origin}/images/electra-hr-logo.png`,
              url: `${origin}/hr-consultancy-uk`,
              telephone: "+44 1622 957300",
              email: "hello@electra-hr.com",
              priceRange: "£££",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Suite 100, 7 Kenden Business Park, Maritime Close",
                addressLocality: "Rochester",
                addressRegion: "Kent",
                postalCode: "ME2 4JF",
                addressCountry: "GB",
              },
              areaServed: { "@type": "Country", name: "United Kingdom" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: origin },
                { "@type": "ListItem", position: 2, name: "HR Consultancy UK", item: `${origin}/hr-consultancy-uk` },
              ],
            },
          ],
        }}
      />

      <section className="grain relative overflow-hidden bg-ink pb-20 pt-32 sm:pt-40 text-paper">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="eyebrow text-pink">HR consultancy, UK-wide</p>
            <LineReveal
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight"
              lines={["Wherever you are,", <span key="l2" className="text-pink">we're in your corner.</span>]}
              delay={0.15}
            />
            <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-paper/70">
              Electra HR works with founders and SMEs of 5 to 50 people across the whole UK, remote-first,
              same-day answers, fixed fees, and in the room when a situation genuinely needs it.
              <Link to="/hr-consultant-kent" className="link-underline font-semibold text-paper hover:text-pink transition-colors">Founded in Kent</Link>. Built for everywhere.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="uk-cta-contact"
                className="group flex items-center gap-2 bg-pink px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-1"
              >
                Book a rapid HR call
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="uk-cta-services"
                className="flex items-center gap-2 border-2 border-white/25 px-7 py-4 font-bold text-paper transition-colors hover:border-pink hover:text-pink"
              >
                See services
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <Reveal delay={0.2}>
              <div className="clip-frame overflow-hidden border border-white/10">
                <BrandImage
                  src="/images/vanessa-challess-900.webp"
                  srcSet="/images/vanessa-challess-480.webp 480w, /images/vanessa-challess-900.webp 900w"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  fallback="/images/vanessa-challess.jpg"
                  alt="Vanessa Challess, founder of Electra HR"
                  className="h-[380px] w-full object-cover object-top"
                />
              </div>
              <p className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-paper/45">
                <MapPin size={14} className="text-pink" /> Rochester HQ · working UK-wide
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow text-ink/50">How national support works</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              National reach, without the call-centre feel
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {WAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div data-testid={`uk-way-${i}`} className="h-full border-2 border-ink p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[8px_8px_0_0_#0F0F1A]">
                  <w.icon size={26} className="text-ink" strokeWidth={1.8} />
                  <h3 className="mt-5 font-display text-xl font-semibold">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/65">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div data-testid="uk-regions" className="mt-14">
              <p className="eyebrow text-ink/50">Where our clients are</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {REGIONS.map((r) => (
                  <span key={r} className="border-2 border-ink/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-ink/60">
                    {r}
                  </span>
                ))}
              </div>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink/55">
                One deliberate choice: we don't pretend to be local everywhere with a page per city.
                You get one honest, consistent consultancy, based in Rochester, working everywhere.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-16 sm:py-24 text-paper">
        <div className="container-x">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow text-pink">What we fix, UK-wide</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                Six services, all fixed-fee
              </h2>
            </div>
            <Link to="/services" data-testid="uk-services-link" className="group flex items-center gap-2 font-bold text-pink">
              All services <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.06} className="bg-ink">
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`uk-service-${s.slug}`}
                  className="group flex h-full flex-col p-7 transition-colors duration-300 hover:bg-ink-soft"
                >
                  <h3 className="font-display text-lg font-semibold group-hover:text-pink transition-colors">{s.title}</h3>
                  <p className="mt-2 text-sm text-paper/55">{s.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {NATIONAL_CASES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <article data-testid={`uk-case-${i}`} className="h-full border border-white/10 bg-ink-soft p-7">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-pink">{c.tag}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-paper/55">{c.result}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-xs text-paper/35">
            Illustrative examples based on typical engagements. Named client stories are on the way.
          </p>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-3xl text-xs leading-relaxed text-paper/40">
              Electra HR is an HR consultancy, not a law firm, and is not SRA-regulated. When a matter
              needs legal advice, representation or privilege protection, our clients are handed straight
              into Bonsai Law, a separate, SRA-regulated firm, with full context and no handoff limbo.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/hr-consultant-kent"
              data-testid="uk-crosslink-kent"
              className="group mt-8 flex items-center justify-between gap-4 border border-white/10 bg-ink-soft p-6 transition-colors duration-300 hover:border-pink/60"
            >
              <div>
                <p className="eyebrow text-pink">Based in Kent?</p>
                <p className="mt-2 text-sm text-paper/70">
                  You're on our home turf, face-to-face support from our Rochester office.
                </p>
              </div>
              <ArrowRight size={22} className="shrink-0 text-pink transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-pink py-16 sm:py-24 text-ink">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="eyebrow text-ink/60">National, not faceless</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
              Wherever you're based, start with a free initial conversation.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">
              No pressure, no hard sell. A straight answer, plus a fixed quote if work is needed,
              within one working day.
            </p>
            <div className="mt-8 space-y-3 text-sm font-medium">
              <a href="tel:+441622957300" data-testid="uk-phone-link" className="flex items-center gap-3 hover:opacity-70 transition-opacity">
                <Phone size={17} /> 01622 957 300
              </a>
              <p className="flex items-center gap-3"><MapPin size={17} /> Rochester HQ · clients UK-wide</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-2 border-ink bg-paper p-7 sm:p-9 shadow-[10px_10px_0_0_#0F0F1A]">
              <LeadForm source="hr-consultancy-uk" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
